import React, { useEffect, useMemo, useState } from "react";
import { Button, SelectBox } from "devextreme-react";
import notify from "devextreme/ui/notify";
import { confirm } from "devextreme/ui/dialog";
import { useAuth } from "../../contexts/auth";
import { isAdminRoleChecker } from "../../utils/roleChecker";
import { USER_ROLES } from "../../constants/roles";
import AccessDenied from "../../components/access-denied/access-denied";
import { syncAllDogs } from "../../api/admin-reload";
import "./admin-other.scss";

const ALL_MONTHS = [
  { name: "январь", val: "01" },
  { name: "февраль", val: "02" },
  { name: "март", val: "03" },
  { name: "апрель", val: "04" },
  { name: "май", val: "05" },
  { name: "июнь", val: "06" },
  { name: "июль", val: "07" },
  { name: "август", val: "08" },
  { name: "сентябрь", val: "09" },
  { name: "октябрь", val: "10" },
  { name: "ноябрь", val: "11" },
  { name: "декабрь", val: "12" },
];

function monthsForYear(year, datePoMax) {
  if (Number(year) === datePoMax.getFullYear()) {
    return ALL_MONTHS.filter((el) => Number(el.val) <= datePoMax.getMonth() + 1);
  }
  return ALL_MONTHS;
}

const AdminOtherPage = () => {
  const { user } = useAuth();
  const isAdmin = isAdminRoleChecker(user, USER_ROLES.ADMIN);
  const [submitting, setSubmitting] = useState(false);
  const [datePoMax, setDatePoMax] = useState(() => new Date());
  const [yearS, setYearS] = useState("");
  const [yearPo, setYearPo] = useState("");
  const [monthS, setMonthS] = useState("");
  const [monthPo, setMonthPo] = useState("");

  useEffect(() => {
    const prevDate = new Date();
    prevDate.setDate(1);
    prevDate.setMonth(prevDate.getMonth() + 2);
    setDatePoMax(new Date(prevDate.getFullYear(), prevDate.getMonth(), 1));
  }, []);

  const yearArray = useMemo(() => {
    const years = [];
    const maxYear = datePoMax.getFullYear();
    for (let i = 2000; i <= maxYear; i++) {
      years.push(i.toString());
    }
    return years;
  }, [datePoMax]);

  useEffect(() => {
    const currDate = new Date();
    const year = currDate.getFullYear().toString();
    const month = (currDate.getMonth() + 1).toString().padStart(2, "0");
    setYearS(year);
    setYearPo(year);
    setMonthS(month);
    setMonthPo(month);
  }, [datePoMax]);

  const monthArrayS = useMemo(
    () => monthsForYear(yearS, datePoMax),
    [yearS, datePoMax]
  );
  const monthArrayPo = useMemo(
    () => monthsForYear(yearPo, datePoMax),
    [yearPo, datePoMax]
  );

  const onYearSChanged = (e) => {
    setYearS(e.value);
    const allowed = monthsForYear(e.value, datePoMax);
    if (!allowed.some((el) => el.val === monthS)) {
      setMonthS(null);
    }
  };

  const onYearPoChanged = (e) => {
    setYearPo(e.value);
    const allowed = monthsForYear(e.value, datePoMax);
    if (!allowed.some((el) => el.val === monthPo)) {
      setMonthPo(null);
    }
  };

  const onUpdate = async () => {
    if (!yearS || !monthS) {
      notify('Выберите "Период С"', "error", 5000);
      return;
    }
    if (!yearPo || !monthPo) {
      notify('Выберите "Период ПО"', "error", 5000);
      return;
    }
    const periodS = Number(yearS + "." + monthS);
    const periodPo = Number(yearPo + "." + monthPo);
    if (periodPo < periodS) {
      notify('"Период по" не может быть меньше "Периода с"!', "error", 5000);
      return;
    }
    const accepted = await confirm(
      "Обновить данные по всем договорам за выбранный период? Каждый договор будет поставлен в очередь один раз.",
      "Обновление данных"
    );
    if (!accepted) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await syncAllDogs(periodS, periodPo);
      if (res.isOk) {
        notify(res.message || "Запрос на обновление отправлен", "success", 10000);
      } else {
        notify(res.message || "Не удалось отправить запрос", "error", 5000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }
  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="admin-other-page content-block dx-card responsive-paddings">
      <h2>Прочее</h2>
      <section className="admin-other-section">
        <h3>Обновление данных по всем договорам</h3>
        <div className="admin-other-periods">
          <div>
            <div className="admin-other-period-title">Период с</div>
            <div className="admin-other-period-fields">
              <SelectBox
                label="Год"
                dataSource={yearArray}
                value={yearS}
                placeholder="Выберите год"
                onValueChanged={onYearSChanged}
                disabled={submitting}
              />
              <SelectBox
                label="Месяц"
                dataSource={monthArrayS}
                displayExpr="name"
                valueExpr="val"
                value={monthS}
                placeholder="Выберите месяц"
                onValueChanged={(e) => setMonthS(e.value)}
                disabled={submitting}
              />
            </div>
          </div>
          <div>
            <div className="admin-other-period-title">Период по</div>
            <div className="admin-other-period-fields">
              <SelectBox
                label="Год"
                dataSource={yearArray}
                value={yearPo}
                placeholder="Выберите год"
                onValueChanged={onYearPoChanged}
                disabled={submitting}
              />
              <SelectBox
                label="Месяц"
                dataSource={monthArrayPo}
                displayExpr="name"
                valueExpr="val"
                value={monthPo}
                placeholder="Выберите месяц"
                onValueChanged={(e) => setMonthPo(e.value)}
                disabled={submitting}
              />
            </div>
          </div>
          <div className="admin-other-submit">
            <Button
              text="Обновить"
              type="default"
              stylingMode="contained"
              disabled={submitting}
              onClick={onUpdate}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminOtherPage;
